// src/components/inventory/StocksList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbHelpers } from '../../db/database';
import { warehousesAPI, stocksAPI } from '../../api';
import apiClient from '../../api/client';
import { useOfflineData } from '../../hooks/useOfflineData';
import { useToast } from '../../hooks/useToast';
import { useModal } from '../../hooks/useModal';
import Modal from '../common/Modal';
import BulkStockUpload from './BulkStockUpload';
import { usePermissions, filterWarehouses } from '../../utils/permissions';
import './StocksList.css';


function StocksList() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, modalConfig, openModal, closeModal } = useModal();
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showBarcodeInput, setShowBarcodeInput] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const { canCreate, canEdit, canDelete } = usePermissions('stocks');

  const { data: stocks, loading: stocksLoading, refetch: refetchStocks } = useOfflineData(
    () => stocksAPI.getAll(),
    dbHelpers.getStocks,
    dbHelpers.saveStocks
  );

  const { data: allWarehouses, loading: warehousesLoading } = useOfflineData(
    () => warehousesAPI.getAll(),
    dbHelpers.getWarehouses
  );

  const loading = stocksLoading || warehousesLoading;

  // ⭐ Fully memoized chain — each step depends on the previous stable reference
  const warehouses = useMemo(() =>
    filterWarehouses(allWarehouses),
    [allWarehouses]
  );

  const accessibleWarehouseIds = useMemo(() =>
    warehouses.map(w => w.id),
    [warehouses]
  );

  const accessibleStocks = useMemo(() =>
    stocks.filter(stock =>
      accessibleWarehouseIds.includes(stock.warehouse_id || stock.warehouse)
    ),
    [stocks, accessibleWarehouseIds]
  );

  useEffect(() => {
    let filtered = accessibleStocks;

    if (warehouseFilter !== 'all') {
      filtered = filtered.filter(s =>
        String(s.warehouse) === String(warehouseFilter) ||
        String(s.warehouse_id) === String(warehouseFilter)
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.warehouse_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredStocks(filtered);
  }, [searchTerm, warehouseFilter, accessibleStocks]);

  async function handleBarcodeLookup(e) {
    e.preventDefault();

    if (!barcodeValue.trim()) {
      toast.error('Please enter a barcode');
      return;
    }

    try {
      const response = await apiClient.get(`/barcode-lookup/?barcode=${barcodeValue.trim()}`);

      if (response.data.type === 'product') {
        const product = response.data.data;
        toast.success(`Found: ${product.name}`);
        setShowBarcodeInput(false);
        setBarcodeValue('');
        navigate(`/inventory/stock-adjustment/${product.id}`);
      } else if (response.data.type === 'variant') {
        const variant = response.data.data;
        toast.success(`Found: ${variant.product_name} - ${variant.color} ${variant.size}`);
        setShowBarcodeInput(false);
        setBarcodeValue('');
        navigate(`/inventory/stock-adjustment/${variant.product}`);
      }
    } catch (error) {
      console.error('Barcode lookup error:', error);
      toast.error('Product not found with that barcode');
    }
  }

  async function handleBulkUploadSuccess() {
    setShowBulkUpload(false);
    if (typeof refetchStocks === 'function') {
      await refetchStocks();
    }
    setRefreshTrigger(prev => prev + 1);
    setTimeout(() => window.location.reload(), 50);
  }

  async function performDelete(stock) {
    setDeleting(stock.id);

    try {
      if (navigator.onLine) {
        try {
          await stocksAPI.delete(stock.id);
        } catch (apiError) {
          if (apiError.response?.status === 400) {
            const errorMessage = apiError.response?.data?.message ||
              apiError.response?.data?.error ||
              'Bad request';

            if (errorMessage.toLowerCase().includes('not found') ||
              errorMessage.toLowerCase().includes('does not exist') ||
              errorMessage.toLowerCase().includes('product')) {
              toast.error(`❌ Cannot delete: Product "${stock.product_name}" no longer exists on server`);
            } else {
              toast.error(`❌ Delete failed (400): ${errorMessage}`);
            }
            throw apiError;
          }

          if (apiError.response?.status === 404) {
            toast.info('Stock not found on server, removing from local database');
          } else {
            throw apiError;
          }
        }
      } else {
        await dbHelpers.markForDeletion('stocks', stock.id);
        toast.info('⚠️ Offline: Stock marked for deletion. Will sync when online.');
      }

      await dbHelpers.deleteStock(stock.id);

      // ✅ Remove from UI state directly — no reload needed
      setFilteredStocks(prev => prev.filter(s => s.id !== stock.id));

      toast.success('✅ Stock entry deleted successfully');
      closeModal();

    } catch (error) {
      let errorMessage = 'Failed to delete stock';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        errorMessage = `Failed to delete (${status}): ${data?.message || data?.error || data?.detail || 'Unknown error'}`;
      } else if (error.request) {
        errorMessage = 'No response from server - check your connection';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }

      toast.error(errorMessage);
    } finally {
      setDeleting(null);
    }
  }

  function handleDelete(stock) {
    openModal({
      title: 'Delete Stock Entry',
      message: `Are you sure you want to delete ${stock.product_name} stock at ${stock.warehouse_name}? This action cannot be undone.`,
      type: 'danger',
      onConfirm: () => performDelete(stock)
    });
  }

  function getStockStatus(quantity, reorderLevel) {
    if (quantity === 0) return 'out-of-stock';
    if (quantity <= reorderLevel) return 'low-stock';
    return 'in-stock';
  }

  if (loading) {
    return (
      <div className="stocks-list">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading stocks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stocks-list fade-in">
      <div className="stocks-header">
        <div>
          <h1>Stock Levels</h1>
          <p className="subtitle">{accessibleStocks.length} stock entries across {warehouses.length} warehouses</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setShowBarcodeInput(true)} className="btn-secondary">
            🔍 Scan Barcode
          </button>

          {canCreate && (
            <button onClick={() => setShowBulkUpload(true)} className="btn-bulk-upload">
              📤 Bulk Upload Stock
            </button>
          )}

          {canCreate && (
            <button onClick={() => navigate('/inventory/stocks/new')} className="btn-primary">
              ➕ Add Stock
            </button>
          )}

          {canEdit && (
            <button onClick={() => navigate('/inventory/stock-transfer')} className="btn-secondary">
              🔄 Transfer Stock
            </button>
          )}
        </div>
      </div>

      <div className="stocks-controls">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search by product or warehouse..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="warehouse-filter"
          value={warehouseFilter}
          onChange={(e) => setWarehouseFilter(e.target.value)}
        >
          <option value="all">All Warehouses</option>
          {warehouses.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
      </div>

      {filteredStocks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p className="empty-title">No stock data found</p>
          <p className="empty-desc">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="stocks-table-container">
          <table className="stocks-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Warehouse</th>
                <th>Quantity</th>
                <th>Reorder Level</th>
                <th>Status</th>
                {(canEdit || canDelete) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredStocks.map(stock => {
                const status = getStockStatus(stock.quantity, stock.reorder_level);
                return (
                  <tr key={stock.id} className={`stock-row ${status}`}>
                    <td className="product-name">
                      {stock.variant_details ? (
                        <>
                          {stock.product_name || 'Unknown Product'}
                          <span style={{ color: '#10b981', fontWeight: '600', marginLeft: '8px' }}>
                            - {stock.variant_details.color}
                            {stock.variant_details.size && ' ' + stock.variant_details.size}
                            {stock.variant_details.material && ` (${stock.variant_details.material})`}
                          </span>
                        </>
                      ) : (
                        <>{stock.product_name || 'Unknown Product'}</>
                      )}
                    </td>
                    <td>{stock.warehouse_name || 'Unknown Warehouse'}</td>
                    <td className="quantity-cell">
                      <span className={`quantity-badge ${status}`}>{stock.quantity}</span>
                    </td>
                    <td>{stock.reorder_level || 'N/A'}</td>
                    <td>
                      {status === 'out-of-stock' && <span className="status-badge out-of-stock">Out of Stock</span>}
                      {status === 'low-stock' && <span className="status-badge low-stock">Low Stock</span>}
                      {status === 'in-stock' && <span className="status-badge in-stock">In Stock</span>}
                    </td>
                    {(canEdit || canDelete) && (
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {canEdit && (
                            <button
                              className="btn-action-small"
                              onClick={() => navigate(`/inventory/stock-adjustment/${stock.product}`)}
                            >
                              Adjust
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn-action-small btn-delete"
                              onClick={() => handleDelete(stock)}
                              disabled={deleting === stock.id}
                            >
                              {deleting === stock.id ? '⏳' : '🗑️'}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showBarcodeInput && (
        <div className="modal-overlay" onClick={() => setShowBarcodeInput(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Barcode Lookup</h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Enter barcode manually or scan with USB barcode scanner
            </p>
            <form onSubmit={handleBarcodeLookup}>
              <input
                type="text"
                value={barcodeValue}
                onChange={(e) => setBarcodeValue(e.target.value)}
                placeholder="Enter or scan barcode..."
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  marginBottom: '1rem'
                }}
              />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary">🔍 Search</button>
                <button
                  type="button"
                  onClick={() => { setShowBarcodeInput(false); setBarcodeValue(''); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkUpload && (
        <BulkStockUpload
          onClose={() => setShowBulkUpload(false)}
          onUploadSuccess={handleBulkUploadSuccess}
        />
      )}

      <Modal isOpen={isOpen} onClose={closeModal} {...modalConfig} />
    </div>
  );
}

export default StocksList;